import React, { useCallback, useEffect, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { ChevronLeft, ChevronRight, Maximize2, X } from 'lucide-react';
import { gallerySlides } from '../data/gallery';
const easeOut = [0.23, 1, 0.32, 1] as const;
export function ProductGallery() {
  const [[index, direction], setState] = useState<[number, number]>([0, 0]);
  const [isZoomed, setIsZoomed] = useState(false);
  const slide = gallerySlides[index];
  const paginate = useCallback((step: number) => {
    setState(([current]) => {
      const next = (current + step + gallerySlides.length) % gallerySlides.length;
      return [next, step];
    });
  }, []);
  const goTo = useCallback((next: number) => {
    setState(([current]) => [next, next > current ? 1 : -1]);
  }, []);
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'ArrowRight') paginate(1);
      if (event.key === 'ArrowLeft') paginate(-1);
      if (event.key === 'Escape') setIsZoomed(false);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [paginate]);
  return <section className="py-20 px-4 bg-cream" data-id="element-216" data-mp="2zssblzvnhgae">
      <div className="max-w-5xl mx-auto" data-id="element-217" data-mp="3dqpcv9l1uave">
        <div className="text-center mb-10" data-id="element-218" data-mp="3dpnwcpl9kw6a">
          <h2 className="text-3xl md:text-4xl font-bold tracking-tight mb-3 text-forest uppercase" data-id="element-219" data-mp="38qulz619xlac">
            See Inside Hydroponics Grower <span className="text-leaf" data-id="element-220" data-mp="2l2l4ggb3fjp0">Ready</span>
          </h2>
          <p className="text-lg text-charcoal/80 max-w-3xl mx-auto" data-id="element-221" data-mp="2h0q3jcx2r8br">
            Real pages from the complete system — hydroponic setups, crop guides, roots, nutrient
            management, pH and EC, lighting, troubleshooting, harvest references and practical
            visual tools.
          </p>
        </div>

        <div className="relative max-w-3xl mx-auto" data-id="element-222" data-mp="22a8r6yiwiy03">
          <div className="relative bg-sage-50 border border-sage-200 rounded-2xl shadow-sm shadow-forest/10 overflow-hidden" role="group" aria-roledescription="carousel" aria-label="Product page previews" data-id="element-223" data-mp="228xpr5cxi2c5">
            <AnimatePresence initial={false} custom={direction} mode="wait" data-id="element-224" data-mp="66ktizfi7hrv">
              <motion.div key={slide.src} custom={direction} initial={{
              opacity: 0,
              x: direction > 0 ? 40 : -40
            }} animate={{
              opacity: 1,
              x: 0
            }} exit={{
              opacity: 0,
              x: direction > 0 ? -40 : 40
            }} transition={{
              duration: 0.25,
              ease: easeOut
            }} drag="x" dragConstraints={{
              left: 0,
              right: 0
            }} dragElastic={0.15} onDragEnd={(_, info) => {
              if (info.offset.x < -60) paginate(1);else if (info.offset.x > 60) paginate(-1);
            }} className="p-4 sm:p-8 cursor-zoom-in touch-pan-y" onClick={() => setIsZoomed(true)} aria-roledescription="slide" aria-label={`${index + 1} of ${gallerySlides.length}`} data-id="element-225" data-mp="azx62b6wry49">
                <img src={slide.src} alt={slide.alt} draggable={false} className="w-full h-auto max-h-[70vh] object-contain mx-auto select-none rounded-lg" data-id="element-226" data-mp="2o527cu1r0bg9" />
              </motion.div>
            </AnimatePresence>

            <button type="button" onClick={() => setIsZoomed(true)} className="absolute top-4 right-4 w-10 h-10 rounded-full bg-forest/90 text-white flex items-center justify-center hover:bg-forest transition-colors duration-150 ease-out focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-leaf" aria-label="View image larger" data-id="element-227" data-mp="29h7f1ernk93p">
              <Maximize2 size={18} data-id="element-228" data-mp="3uszd41hqkh3l" />
            </button>
          </div>

          <button type="button" onClick={() => paginate(-1)} className="absolute left-0 sm:-left-5 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-leaf text-white border-2 border-white shadow-lg flex items-center justify-center hover:bg-forest transition-colors duration-150 ease-out focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-leaf focus-visible:ring-offset-2" aria-label="Previous image" data-id="element-229" data-mp="3pv7bgfrahgm7">
            <ChevronLeft size={24} data-id="element-230" data-mp="2ik9jepawmrg8" />
          </button>
          <button type="button" onClick={() => paginate(1)} className="absolute right-0 sm:-right-5 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-leaf text-white border-2 border-white shadow-lg flex items-center justify-center hover:bg-forest transition-colors duration-150 ease-out focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-leaf focus-visible:ring-offset-2" aria-label="Next image" data-id="element-231" data-mp="23shdf3x02pr6">
            <ChevronRight size={24} data-id="element-232" data-mp="1puaqgitlaa6a" />
          </button>
        </div>

        <div className="flex justify-center items-center gap-3 mt-6" data-id="element-233" data-mp="1pvlrwbzkb5u8">
          {gallerySlides.map((item, dotIndex) => <button key={item.src} type="button" onClick={() => goTo(dotIndex)} className={`h-2.5 rounded-full transition-[width,background-color] duration-200 ease-out focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-leaf ${dotIndex === index ? 'w-8 bg-leaf' : 'w-2.5 bg-forest/25 hover:bg-forest/40'}`} aria-label={`Go to image ${dotIndex + 1}`} aria-current={dotIndex === index} data-id="element-234" data-mp="1194p2oyv1xz2" data-mpi={dotIndex} />)}
        </div>

        <p className="text-center text-lg font-bold text-forest mt-10" data-id="element-235" data-mp="1yvv6x29vdqj4">
          Real pages. Real growing tools.{' '}
          <span className="text-leaf" data-id="element-236" data-mp="1tzwgl94zdyg2">
            A system designed to be used while you grow — not simply read.
          </span>
        </p>
      </div>

      <AnimatePresence data-id="element-237" data-mp="23x12r8ytttwv">
        {isZoomed && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.2,
        ease: easeOut
      }} className="fixed inset-0 z-50 bg-forest-700/90 flex items-center justify-center p-4 overflow-auto" role="dialog" aria-modal="true" aria-label={slide.alt} onClick={() => setIsZoomed(false)} data-id="element-238" data-mp="2ayxy03ko8mdd">
            <button type="button" onClick={() => setIsZoomed(false)} className="fixed top-4 right-4 w-11 h-11 rounded-full bg-white/10 text-white flex items-center justify-center hover:bg-white/20 transition-colors duration-150 ease-out focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-lime-soft" aria-label="Close enlarged image" data-id="element-239" data-mp="2tomdqpfybw85">
              <X size={22} data-id="element-240" data-mp="19bexy7itbekw" />
            </button>
            <motion.img initial={{
          opacity: 0,
          scale: 0.97
        }} animate={{
          opacity: 1,
          scale: 1
        }} exit={{
          opacity: 0,
          scale: 0.97
        }} transition={{
          duration: 0.2,
          ease: easeOut
        }} src={slide.src} alt={slide.alt} className="max-w-full md:max-w-3xl h-auto rounded-xl shadow-2xl" onClick={event => event.stopPropagation()} data-id="element-241" data-mp="1e85fz0e6gk8y" />
          </motion.div>}
      </AnimatePresence>
    </section>;
}